import React, { useState, useEffect } from 'react';
import { TopicSubmission, Team } from '../types/index.ts';
import { topicApi } from '../services/api.ts';
import { authService } from '../services/auth.ts';

interface TopicSubmissionModalProps {
  onClose: () => void;
  onTopicSubmitted: (teamId: string) => void;
  sessionSettings?: { sessionDate: string; sessionTime: string } | null;
  currentTeam?: Team;
}

const TopicSubmissionModal: React.FC<TopicSubmissionModalProps> = ({ 
  onClose, 
  onTopicSubmitted, 
  sessionSettings, 
  currentTeam 
}) => {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [presenterAlias, setPresenterAlias] = useState('');
  const [sessionType, setSessionType] = useState<'next' | 'future' | 'past'>('next');
  const [presentedDate, setPresentedDate] = useState('');
  const [estimatedPresentationDate, setEstimatedPresentationDate] = useState('');
  const [selectedTeamId, setSelectedTeamId] = useState(currentTeam?.teamId || '');
  const [teams, setTeams] = useState<Team[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [aliasError, setAliasError] = useState('');
  const [duration, setDuration] = useState('');
  const [geoRegion, setGeoRegion] = useState('NAMER');
  const [recordingUrl, setRecordingUrl] = useState('');
  const [builderSpaceUrl, setBuilderSpaceUrl] = useState('');
  const [gitlabUrl, setGitlabUrl] = useState('');
  const [urlError, setUrlError] = useState('');
  const [builderSpaceUrlError, setBuilderSpaceUrlError] = useState('');
  const [gitlabUrlError, setGitlabUrlError] = useState('');
  const [selectedTeamSessionSettings, setSelectedTeamSessionSettings] = useState<{ sessionDate: string; sessionTime: string } | null>(sessionSettings || null);

  useEffect(() => {
    loadTeams();
    
    // Pre-populate with current user's alias
    const userInfo = authService.getUserInfo();
    if (userInfo?.username) {
      setPresenterAlias(userInfo.username);
    }
  }, []);

  // Load session settings when selected team changes
  useEffect(() => {
    if (selectedTeamId) {
      loadSessionSettings(selectedTeamId);
    }
  }, [selectedTeamId]);

  const loadSessionSettings = async (teamId: string) => {
    try {
      const settings = await topicApi.getSessionSettings(teamId);
      setSelectedTeamSessionSettings(settings);
    } catch (error) {
      console.error('Failed to load session settings:', error);
      setSelectedTeamSessionSettings(null);
    }
  };

  const loadTeams = async () => {
    try {
      const teamsData = await topicApi.getTeams();
      setTeams(teamsData);
    } catch (error) {
      console.error('Failed to load teams:', error);
    }
  };

  const formatSessionDisplay = () => {
    if (!selectedTeamSessionSettings?.sessionDate || !selectedTeamSessionSettings?.sessionTime) {
      return 'TBD';
    }
    
    const [year, month, day] = selectedTeamSessionSettings.sessionDate.split('-').map(Number);
    const [hours, minutes] = selectedTeamSessionSettings.sessionTime.split(':').map(Number);
    const utcDateTime = new Date(Date.UTC(year, month - 1, day, hours, minutes, 0, 0));
    
    const dateStr = utcDateTime.toLocaleDateString('en-US', { 
      weekday: 'long',
      year: 'numeric', 
      month: 'long', 
      day: 'numeric',
      timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone
    });
    
    const timeStr = utcDateTime.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
      timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      timeZoneName: 'short'
    });
    
    return `${dateStr} at ${timeStr}`;
  };

  const validateAlias = (alias: string): string => {
    if (!alias.trim()) {
      return 'Presenter aliases required';
    }
    
    // Split by comma, trim, filter empty
    const aliases = alias.split(',')
      .map(a => a.trim().toLowerCase())
      .filter(a => a.length > 0);
    
    // Check for duplicates
    const unique = [...new Set(aliases)];
    if (unique.length !== aliases.length) {
      return 'Duplicate presenter aliases detected';
    }
    
    // Validate count
    if (unique.length > 10) {
      return 'Maximum 10 presenters allowed';
    }
    
    // Validate each alias
    for (const a of unique) {
      if (a.length > 12) {
        return `Alias "${a}" exceeds 12 characters`;
      }
      if (!/^[a-z]+$/.test(a)) {
        return `Alias "${a}" must contain only lowercase letters`;
      }
    }
    
    return '';
  };

  const validateUrl = (url: string): string => {
    if (!url.trim()) {
      return ''; // URL is optional
    }
    try {
      new URL(url);
      return '';
    } catch {
      return 'Please enter a valid URL';
    }
  };

  const handleAliasBlur = () => {
    const error = validateAlias(presenterAlias);
    setAliasError(error);
  };

  const handleUrlBlur = () => {
    const error = validateUrl(recordingUrl);
    setUrlError(error);
  };

  const handleBuilderSpaceUrlBlur = () => {
    const error = validateUrl(builderSpaceUrl);
    setBuilderSpaceUrlError(error);
  };

  const handleGitlabUrlBlur = () => {
    const error = validateUrl(gitlabUrl);
    setGitlabUrlError(error);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    const aliasValidationError = validateAlias(presenterAlias);
    const urlValidationError = validateUrl(recordingUrl);
    const builderSpaceUrlValidationError = validateUrl(builderSpaceUrl);
    const gitlabUrlValidationError = validateUrl(gitlabUrl);

    if (!title.trim() || !description.trim() || aliasValidationError || urlValidationError || builderSpaceUrlValidationError || gitlabUrlValidationError) {
      setError('All fields are required and must be valid');
      setAliasError(aliasValidationError);
      setUrlError(urlValidationError);
      setBuilderSpaceUrlError(builderSpaceUrlValidationError);
      setGitlabUrlError(gitlabUrlValidationError);
      return;
    }

    // Validate presentation date if marking as already presented
    if (sessionType === 'past') {
      if (!presentedDate) {
        setError('Please select a presentation date');
        return;
      }
      
      const selectedDate = new Date(presentedDate + 'T00:00:00');
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      
      if (selectedDate > today) {
        setError('Presentation date cannot be in the future');
        return;
      }
    }

    setIsSubmitting(true);
    setError('');

    try {
      const topic: TopicSubmission = {
        title: title.trim(),
        description: description.trim(),
        presenterAlias: presenterAlias.trim(),
        sessionType,
        teamId: selectedTeamId,
        ...(duration !== '' ? { duration: Number(duration) } : {}),
        geoRegion,
        ...(sessionType === 'future' && estimatedPresentationDate ? { estimatedPresentationDate } : {})
      };

      // Add URL fields
      (topic as any).recordingUrl = recordingUrl.trim();
      (topic as any).builderSpaceUrl = builderSpaceUrl.trim();
      (topic as any).gitlabUrl = gitlabUrl.trim();

      // Add presentedAt if marking as already presented
      if (sessionType === 'past' && presentedDate) {
        (topic as any).presentedAt = new Date(presentedDate + 'T12:00:00.000Z').toISOString();
      }

      await topicApi.submitTopic(topic);
      onTopicSubmitted(selectedTeamId);
      onClose();
    } catch (error: any) {
      setError(error.response?.data?.error || 'Failed to submit topic');
      setIsSubmitting(false);
    }
  };

  const selectedTeam = teams.find(team => team.teamId === selectedTeamId);

  return (
    <div className="modal-overlay">
      <div className="modal">
        <h3>Submit New Topic</h3>
        <form onSubmit={handleSubmit}>
          <div style={{ fontSize: '12px', color: '#b45309', backgroundColor: '#fef9c3', padding: '8px 12px', borderRadius: '4px', marginBottom: '16px', border: '1px solid #fde68a' }}>
            ⚠️ Do not enter any customer, private, or sensitive information.
          </div>
          <div className="form-group">
            <label><span style={{ color: 'red' }}>*</span>Title:</label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Enter title of topic or demo"
              required
            />
          </div>

          <div className="form-group">
            <label><span style={{ color: 'red' }}>*</span>Description:</label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder={"Describe what you'll present. Optionally as relevant, include:\n\"Challenge\" your demo/topic solves\n\"Solution\" you're discussing\n\"Capabilities\" of your solution\n\"Call to Action\" for attendees"}
              rows={6}
              maxLength={2000}
              required
            />
          </div>

          <div className="form-group">
            <label><span style={{ color: 'red' }}>*</span>Presenter Aliases (comma-separated):</label>
            <input
              type="text"
              value={presenterAlias}
              onChange={(e) => setPresenterAlias(e.target.value.toLowerCase())}
              onBlur={handleAliasBlur}
              placeholder="e.g., alice,bob,charlie (max 10)"
              required
            />
            {aliasError && <div className="error-message" style={{ fontSize: '14px', marginTop: '4px' }}>{aliasError}</div>}
            <div style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>
              Enter aliases separated by commas (no @ symbol). Maximum 10 presenters.
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="geoRegion">Your Geo/Region (optional):</label>
            <select
              id="geoRegion"
              value={geoRegion}
              onChange={(e) => setGeoRegion(e.target.value)}
            >
              <option value="NAMER">NAMER</option>
              <option value="LATAM">LATAM</option>
              <option value="GCR">GCR</option>
              <option value="EMEA">EMEA</option>
              <option value="APJ">APJ</option>
              <option value="Other">Other</option>
            </select>
          </div>

          <div className="form-group">
            <label htmlFor="duration">Estimated Duration (optional):</label>
            <select
              id="duration"
              value={duration}
              onChange={(e) => setDuration(e.target.value)}
            >
              <option value="">Unknown</option>
              <option value="5">5 min</option>
              <option value="10">10 min</option>
              <option value="15">15 min</option>
              <option value="20">20 min</option>
              <option value="25">25 min</option>
              <option value="30">30 min</option>
              <option value="45">45 min</option>
              <option value="60">60 min</option>
            </select>
          </div>

          <div className="form-group">
            <label>Submit for Series:</label>
            <select
              value={selectedTeamId}
              onChange={(e) => setSelectedTeamId(e.target.value)}
              required
            >
              <option value="">Select a series...</option>
              {teams.map((team) => (
                <option key={team.teamId} value={team.teamId}>
                  {team.teamName}
                </option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label htmlFor="sessionType">Session:</label>
            <select
              id="sessionType"
              value={sessionType}
              onChange={(e) => {
                const newSessionType = e.target.value as 'next' | 'future' | 'past';
                setSessionType(newSessionType);
                // Set default date when selecting "Already Presented"
                if (newSessionType === 'past' && !presentedDate) {
                  setPresentedDate(new Date().toISOString().split('T')[0]);
                }
                // Clear estimated presentation date when switching away from "future"
                if (newSessionType !== 'future') {
                  setEstimatedPresentationDate('');
                }
              }}
              disabled={!selectedTeamId}
              required
            >
              <option value="next">
                {selectedTeamSessionSettings?.sessionDate && selectedTeamSessionSettings?.sessionTime 
                  ? formatSessionDisplay() 
                  : `Next Session for ${selectedTeam?.teamName} (${formatSessionDisplay()})`}
              </option>
              <option value="future">Future Session (Date TBD)</option>
              <option value="past">Already Presented</option>
            </select>
          </div>

          {/* Show date picker when "Future Session" is selected */}
          {sessionType === 'future' && (
            <div className="form-group">
              <label htmlFor="estimated-presentation-date">Estimated Presentation Date (optional):</label>
              <input
                id="estimated-presentation-date"
                type="date"
                value={estimatedPresentationDate}
                onChange={(e) => setEstimatedPresentationDate(e.target.value)}
                style={{
                  width: '180px',
                  padding: '8px',
                  fontSize: '14px',
                  border: '1px solid #ccc',
                  borderRadius: '4px'
                }}
              />
            </div>
          )}

          {/* Show date picker when "Already Presented" is selected */}
          {sessionType === 'past' && (
            <div className="form-group">
              <label htmlFor="presented-date"><span style={{ color: 'red' }}>*</span>Presentation Date:</label>
              <input
                id="presented-date"
                type="date"
                value={presentedDate}
                onChange={(e) => setPresentedDate(e.target.value)}
                max={new Date().toISOString().split('T')[0]}
                required
                style={{
                  width: '180px',
                  padding: '8px',
                  fontSize: '14px',
                  border: '1px solid #ccc',
                  borderRadius: '4px'
                }}
              />
            </div>
          )}

          {/* URL fields grouped in a subtle gray box */}
          <div style={{ background: '#f8f8f8', border: '1px solid #e0e0e0', borderRadius: '6px', padding: '12px', marginBottom: '8px' }}>
            <div style={{ fontSize: '12px', color: '#888', marginBottom: '10px', fontStyle: 'italic' }}>Optional, can add later</div>
            <div className="form-group" style={{ marginBottom: '12px' }}>
              <label htmlFor="recordingUrl" style={{ fontSize: '14px' }}>Recording URL (Broadcast or other):</label>
              <input
                type="url"
                id="recordingUrl"
                value={recordingUrl}
                onChange={(e) => setRecordingUrl(e.target.value)}
                onBlur={handleUrlBlur}
                placeholder="https://broadcast.amazon.com/recording"
              />
              {urlError && <div className="error-message" style={{ fontSize: '14px', marginTop: '4px' }}>{urlError}</div>}
            </div>

            <div className="form-group" style={{ marginBottom: '12px' }}>
              <label htmlFor="builderSpaceUrl" style={{ fontSize: '14px' }}>Project Content URL (Scoby or other):</label>
              <input
                type="url"
                id="builderSpaceUrl"
                value={builderSpaceUrl}
                onChange={(e) => setBuilderSpaceUrl(e.target.value)}
                onBlur={handleBuilderSpaceUrlBlur}
                placeholder="https://scoby.apdf.people.aws.dev"
              />
              {builderSpaceUrlError && <div className="error-message" style={{ fontSize: '14px', marginTop: '4px' }}>{builderSpaceUrlError}</div>}
            </div>

            <div className="form-group" style={{ marginBottom: '0' }}>
              <label htmlFor="gitlabUrl" style={{ fontSize: '14px' }}>Code repo URL (Gitlab or other):</label>
              <input
                type="url"
                id="gitlabUrl"
                value={gitlabUrl}
                onChange={(e) => setGitlabUrl(e.target.value)}
                onBlur={handleGitlabUrlBlur}
                placeholder="https://gitlab.aws.dev/project"
              />
              {gitlabUrlError && <div className="error-message" style={{ fontSize: '14px', marginTop: '4px' }}>{gitlabUrlError}</div>}
            </div>
          </div>

          {error && <div className="error-message">{error}</div>}

          <div className="modal-actions">
            <button type="button" onClick={onClose} className="cancel-button">
              Cancel
            </button>
            <button type="submit" disabled={isSubmitting} className="submit-button">
              {isSubmitting ? 'Submitting...' : 'Submit Topic'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default TopicSubmissionModal;
